import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchSitesForOwner,
  createSite,
  deleteSite,
  fetchOfficers,
  fetchCheckIns,
  fetchIncidents,
  fetchCheckpoints,
  createIncident,
  subscribeToTable,
  claimSiteByCode,
  type DbSite,
  type DbOfficer,
  type DbCheckIn,
  type DbIncident,
  type DbCheckpoint,
} from "@/lib/supabaseQueries";
import type { Database } from "@/integrations/supabase/types";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

export function useSiteOwnerData() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id;
  const initialLoadDone = useRef(false);

  const sitesQuery = useQuery({
    queryKey: ["sites", "owner", userId],
    queryFn: () => fetchSitesForOwner(userId!),
    enabled: !!userId,
  });

  const officersQuery = useQuery({
    queryKey: ["officers"],
    queryFn: fetchOfficers,
    refetchInterval: 10000, // Poll every 10s as realtime fallback for status sync
  });

  const checkInsQuery = useQuery({
    queryKey: ["checkIns"],
    queryFn: fetchCheckIns,
  });

  const incidentsQuery = useQuery({
    queryKey: ["incidents"],
    queryFn: fetchIncidents,
  });

  const checkpointsQuery = useQuery({
    queryKey: ["checkpoints"],
    queryFn: fetchCheckpoints,
  });

  // Mark initial load complete after first data arrives
  useEffect(() => {
    if (sitesQuery.data && officersQuery.data && checkpointsQuery.data && !initialLoadDone.current) {
      // Small delay to avoid toasting on initial fetch
      setTimeout(() => { initialLoadDone.current = true; }, 1500);
    }
  }, [sitesQuery.data, officersQuery.data, checkpointsQuery.data]);

  // Real-time subscriptions with toast notifications
  useEffect(() => {
    const unsubs = [
      subscribeToTable("sites", () => qc.invalidateQueries({ queryKey: ["sites"] })),
      subscribeToTable("officers", (payload: any) => {
        qc.invalidateQueries({ queryKey: ["officers"] });
        if (initialLoadDone.current && payload?.eventType === "INSERT") {
          const name = payload.new?.name || "Unknown";
          toast.info("👮 New Officer Added", {
            description: `${name} has been assigned to your site.`,
            duration: 5000,
          });
        }
        if (initialLoadDone.current && payload?.eventType === "DELETE") {
          toast.warning("Officer Removed", {
            description: "An officer has been removed from your site.",
            duration: 5000,
          });
        }
      }),
      subscribeToTable("check_ins", () => qc.invalidateQueries({ queryKey: ["checkIns"] })),
      subscribeToTable("incidents", (payload: any) => {
        qc.invalidateQueries({ queryKey: ["incidents"] });
        if (initialLoadDone.current && payload?.eventType === "INSERT") {
          const severity = payload.new?.severity || "low";
          const title = payload.new?.title || "New incident";
          toast.error("🚨 New Incident Reported", {
            description: `${title} (${severity} severity)`,
            duration: 6000,
          });
        }
      }),
      subscribeToTable("checkpoints", (payload: any) => {
        qc.invalidateQueries({ queryKey: ["checkpoints"] });
        if (initialLoadDone.current && payload?.eventType === "INSERT") {
          const name = payload.new?.name || "Unknown";
          toast.info("📍 New Checkpoint Added", {
            description: `Checkpoint "${name}" has been added to your site.`,
            duration: 5000,
          });
        }
        if (initialLoadDone.current && payload?.eventType === "DELETE") {
          toast.warning("Checkpoint Removed", {
            description: "A checkpoint has been removed from your site.",
            duration: 5000,
          });
        }
      }),
    ];
    return () => unsubs.forEach((u) => u());
  }, [qc]);

  const addSite = useMutation({
    mutationFn: (site: { name: string; address: string }) =>
      createSite({ name: site.name, address: site.address, owner_id: userId! }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sites"] }),
  });

  const removeSite = useMutation({
    mutationFn: deleteSite,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sites"] }),
  });

  const claimSite = useMutation({
    mutationFn: (code: string) => claimSiteByCode(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sites"] }),
  });

  const addIncident = useMutation({
    mutationFn: (incident: Database["public"]["Tables"]["incidents"]["Insert"]) => createIncident(incident),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incidents"] }),
  });

  const sites = sitesQuery.data ?? [];
  const siteIds = new Set(sites.map((s) => s.id));
  const officers = (officersQuery.data ?? []).filter((o) => o.site_id && siteIds.has(o.site_id));
  const checkIns = (checkInsQuery.data ?? []).filter((c) => c.site_id && siteIds.has(c.site_id));
  const incidents = (incidentsQuery.data ?? []).filter((i) => i.site_id && siteIds.has(i.site_id));
  const checkpoints = (checkpointsQuery.data ?? []).filter((cp) => siteIds.has(cp.site_id));

  return {
    sites,
    officers,
    checkIns,
    incidents,
    checkpoints,
    isLoading: sitesQuery.isLoading || officersQuery.isLoading,
    addSite,
    removeSite,
    claimSite,
    addIncident,
  };
}
